import { Button, message } from 'antd'
import { FeatureFormMode } from 'components/form/formTypes'
import { CoordinateInputTablePointAndClickApi } from 'components/form/inputs/coordinatesInput/CoordinatesInputTable'
import { Coordinate } from 'components/form/inputs/coordinatesInput/helpers/coordinateTranslations'
import { EditableFeatureForm } from 'features/assetDatabase/components/EditableFeatureForm'
import { getFeatureTableOptionsByType } from 'features/assetDatabase/utils/assetDBFeatures'
import { AssetModelFeatureClassMap } from 'features/assetModeling'
import { useGeckoLocale } from 'features/locale'
import { useGeckoPermissions } from 'features/permissions'
import {
  EditFeatureTable,
  SelectedFeature,
} from 'features/rightDrawer/drawerStates/crudWorkflows/editRichAssetModel/features/EditFeatureTable'
import React, { Fragment, useState } from 'react'
import styled from 'styled-components'
import {
  componentAvailableFeatures,
  GeckoAssetModel,
  GeckoComponent,
  GeckoComponentModelJSON,
  GeckoComponentModelType,
  GeckoEditableFeature,
  GeckoFeatureModelType,
  organizeFeaturesByType,
  regenerateAssetModel,
  RolodexEntryPutJson,
  RolodexEntryType,
} from 'types'
import { UpdaterFunction } from 'types/generics/updaterFunction'

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
  width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
`

const StyledReturnButton = styled(Button)`
  background-color: transparent;
  border: 1px solid grey;
`

interface FeaturesTabInterface {
  selectedComponentType: GeckoComponentModelType
  selectedComponentJSON: GeckoComponentModelJSON
  assetModel: GeckoAssetModel
  component: GeckoComponent
  handleSaveToRolodex: (entries: RolodexEntryPutJson[]) => void
  jsonSelector: React.ReactNode
  assetModelFeatureTypeFilter?: GeckoFeatureModelType | GeckoFeatureModelType[]
  onFeatureSelected: UpdaterFunction<string | undefined> | null
  selectedFeatureId: string | undefined
  pointAndClickApi: CoordinateInputTablePointAndClickApi | undefined
  highlightCoordinate: (coordinate: Coordinate | null) => void
  displayCoordinateUpdatesApi: {
    display: (newCoords: ReadonlyArray<Coordinate>) => void
    clear: () => void
  }
}

export const FeaturesTab = ({
  selectedComponentType,
  selectedComponentJSON,
  assetModel,
  component,
  handleSaveToRolodex,
  jsonSelector,
  assetModelFeatureTypeFilter,
  onFeatureSelected,
  selectedFeatureId,
  pointAndClickApi,
  highlightCoordinate,
  displayCoordinateUpdatesApi,
}: FeaturesTabInterface) => {
  const [messageApi, contextHolder] = message.useMessage()
  const { measurementSystem } = useGeckoLocale()
  const { permissions } = useGeckoPermissions()

  const [featureInEditMode, setFeatureInEditMode] = useState<
    SelectedFeature | undefined
  >(undefined)

  const availableFeatureTypes = React.useMemo(
    () => componentAvailableFeatures(selectedComponentType),
    [selectedComponentType]
  )

  const selectedComponentFeaturesJSON = React.useMemo(
    () => organizeFeaturesByType(selectedComponentJSON, availableFeatureTypes),
    [selectedComponentJSON, availableFeatureTypes]
  )

  // Filter feature types and features by category if assetModelFeatureTypeFilter is provided (primarily for Ship decks)
  const filteredFeatureTypes = React.useMemo(() => {
    if (!assetModelFeatureTypeFilter) {
      return availableFeatureTypes
    }

    const filterTypes = Array.isArray(assetModelFeatureTypeFilter)
      ? assetModelFeatureTypeFilter
      : [assetModelFeatureTypeFilter]

    return availableFeatureTypes.filter((featureType) =>
      filterTypes.includes(featureType)
    )
  }, [availableFeatureTypes, assetModelFeatureTypeFilter])

  const filteredComponentFeaturesJSON = React.useMemo(() => {
    if (!assetModelFeatureTypeFilter) {
      return selectedComponentFeaturesJSON
    }

    const filtered: typeof selectedComponentFeaturesJSON = {}
    for (const featureType of filteredFeatureTypes) {
      if (selectedComponentFeaturesJSON[featureType]) {
        filtered[featureType] = selectedComponentFeaturesJSON[featureType]
      }
    }
    return filtered
  }, [
    selectedComponentFeaturesJSON,
    filteredFeatureTypes,
    assetModelFeatureTypeFilter,
  ])

  const deleteFeature = React.useCallback(
    (featureID: string) => {
      const rawAssetModel = assetModel.raw
      const rawAssetModelComponents: Record<string, unknown> = rawAssetModel
        .data.components as Record<string, unknown>
      if (!rawAssetModelComponents[component.id]) {
        messageApi.error(
          'Unable to delete this feature. Could not find the Component ID on this Asset Model!'
        )
        return
      }

      const rawAssetModelComponent: Record<string, unknown> =
        rawAssetModelComponents[component.id] as Record<string, unknown>
      const rawAssetModelComponentFeatures: Record<string, unknown> =
        rawAssetModelComponent.features as Record<string, unknown>

      if (!rawAssetModelComponentFeatures[featureID]) {
        messageApi.error(
          'Unable to delete this feature. Could not find the Feature ID on this Component Model!'
        )
        return
      }

      delete rawAssetModelComponentFeatures[featureID]
      const regeneratedAssetModel = regenerateAssetModel(
        GeckoAssetModel.CreateFromJSON(rawAssetModel)
      )
      const rolodexPutEntry: RolodexEntryPutJson = {
        id: regeneratedAssetModel.id,
        type: RolodexEntryType.ASSET_MODEL,
        data: regeneratedAssetModel.data,
        tags: regeneratedAssetModel.tags,
      }
      handleSaveToRolodex([rolodexPutEntry])
    },
    [assetModel, component, handleSaveToRolodex, messageApi]
  )

  // For each Feature Type we have, get the rendered table for that type
  const featureTables = React.useMemo(() => {
    return filteredFeatureTypes.map((featureType) => {
      const features = filteredComponentFeaturesJSON[featureType] ?? {}
      const featureClass = AssetModelFeatureClassMap.get(featureType) as
        | GeckoEditableFeature
        | undefined

      if (!featureClass) {
        throw new Error(
          `Unable to build Feature Edit Tables. Feature Type ${featureType} does not have a mapped class`
        )
      }

      const featureOptions = getFeatureTableOptionsByType(featureType)

      return (
        <Fragment key={featureType}>
          {featureOptions && (
            <EditFeatureTable
              tableTitle={
                featureOptions.title + ` (${Object.keys(features).length})`
              }
              featureJSONMap={features}
              fieldsToShow={featureOptions.fields}
              featureClass={featureClass}
              featureType={featureType}
              sortBy={featureOptions.sortBy}
              onFeatureEditClicked={setFeatureInEditMode}
              deleteFeature={deleteFeature}
              disableEditing={featureOptions.disableEditing}
              tableMessage={featureOptions.tableMessage}
              selectedFeatureId={selectedFeatureId}
              onFeatureSelected={onFeatureSelected}
              // Ship Decks: these can default open because there are no other categories in these feature tabs
              defaultOpen={
                assetModelFeatureTypeFilter ===
                  GeckoFeatureModelType.ShipDeckCompartment ||
                assetModelFeatureTypeFilter ===
                  GeckoFeatureModelType.PolygonalPlate
              }
            />
          )}
        </Fragment>
      )
    })
  }, [
    filteredFeatureTypes,
    filteredComponentFeaturesJSON,
    deleteFeature,
    selectedFeatureId,
    onFeatureSelected,
    assetModelFeatureTypeFilter,
  ])

  if (featureInEditMode) {
    const { featureID, featureJSON, featureType } = featureInEditMode

    const formMode = featureID ? FeatureFormMode.Edit : FeatureFormMode.Add

    return (
      <StyledContainer>
        <StyledReturnButton
          onClick={() => {
            setFeatureInEditMode(undefined)
            onFeatureSelected?.(undefined)
          }}>
          Return to Feature Selection
        </StyledReturnButton>
        <EditableFeatureForm
          measurementSystem={measurementSystem}
          featureType={featureType}
          featureJSON={featureJSON}
          featureID={featureID}
          formMode={formMode}
          assetModel={assetModel}
          selectedComponent={component}
          onSave={(editedAssetModel) => {
            handleSaveToRolodex([editedAssetModel])
            setFeatureInEditMode(undefined)
          }}
          userPermissions={permissions}
          jsonSelector={jsonSelector}
          pointAndClickApi={pointAndClickApi}
          highlightCoordinate={highlightCoordinate}
          displayCoordinateUpdatesApi={displayCoordinateUpdatesApi}
        />
      </StyledContainer>
    )
  }

  return (
    <>
      {contextHolder}
      {featureTables}
    </>
  )
}
